import { useEffect, useRef } from 'react'
import { AppsRoutes } from '../getRoute'
import { navigate } from '../useAppsNavigate'
import { staticState } from '@wellesley/host-remote/common/staticState'
import { normalizeAtPathForBrowser } from '../pathNormalizer'

const isAbsoluteHref = (href: string) => href.startsWith('http')

const isInternalLink = (link: HTMLAnchorElement) => {
  const hrefAttr = link.getAttribute('href') || ''

  return link.id.startsWith('footnote-markdown') || hrefAttr.startsWith('#')
}

const getLinks = (container: HTMLDivElement) => {
  const links = container.querySelectorAll('a')
  return Array.from(links)
}

const setHref = (link: HTMLAnchorElement, baseUrl: string) => {
  const { pathname } = link.href ? new URL(link.href) : { pathname: '' }

  if (link.href && !isAbsoluteHref(link.getAttribute('href') || ''))
    link.href = normalizeAtPathForBrowser(`${baseUrl}${pathname}`)
}

const handleLinkClick = (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()

  if (e.currentTarget instanceof HTMLAnchorElement) {
    const href = e.currentTarget.href
    const { pathname } = href ? new URL(href) : { pathname: '' }
    const mappedPathname = pathname.replace(/\/@([^/]+)/g, '/@/$1')

    if (e.ctrlKey || e.metaKey || isAbsoluteHref(href)) {
      navigate(href as AppsRoutes, { target: '_blank' })
    } else {
      navigate(mappedPathname as AppsRoutes)
    }
  }
}

type ExternalLinksProps = {
  baseUrl?: string
  className?: string
  children: React.ReactNode
}

export const ExternalLinks = ({
  baseUrl = staticState.get().baseUrl,
  children,
  className,
}: ExternalLinksProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      const links = getLinks(containerRef.current)

      links.forEach((link) => {
        if (isInternalLink(link)) return

        setHref(link, baseUrl)
        link.addEventListener('click', handleLinkClick)
      })

      return () => {
        links.forEach((link) => {
          link.removeEventListener('click', handleLinkClick)
        })
      }
    }
  }, [])

  return (
    <div data-testid="external-links" ref={containerRef} className={className}>
      {children}
    </div>
  )
}
